import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import { usePricingBilling } from "@/components/PricingBillingContext";

type BillingPeriod = "monthly" | "yearly";

interface PricingExplorerState {
  billingPeriod: BillingPeriod;
  setBillingPeriod: (period: BillingPeriod) => void;
  selectedPlanIndex: number;
  selectPlan: (index: number) => void;
  hoveredPlanIndex: number | null;
  setHoveredPlanIndex: (index: number | null) => void;
  planCount: number;
}

const PricingExplorerContext = createContext<PricingExplorerState | null>(null);

interface PricingExplorerProviderProps {
  children: ReactNode;
  defaultBillingPeriod?: BillingPeriod;
  defaultSelectedPlanIndex?: number;
  planCount: number;
}

export const PricingExplorerProvider = ({
  children,
  defaultBillingPeriod = "monthly",
  defaultSelectedPlanIndex = 0,
  planCount,
}: PricingExplorerProviderProps) => {
  const sharedBilling = usePricingBilling();
  const [localBillingPeriod, setLocalBillingPeriod] =
    useState<BillingPeriod>(defaultBillingPeriod);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(
    defaultSelectedPlanIndex
  );
  const [hoveredPlanIndex, setHoveredPlanIndex] = useState<number | null>(null);

  const billingPeriod = sharedBilling?.billingPeriod ?? localBillingPeriod;
  const setBillingPeriod =
    sharedBilling?.setBillingPeriod ?? setLocalBillingPeriod;

  const selectPlan = setSelectedPlanIndex;

  return (
    <PricingExplorerContext.Provider
      value={{
        billingPeriod,
        setBillingPeriod,
        selectedPlanIndex,
        selectPlan,
        hoveredPlanIndex,
        setHoveredPlanIndex,
        planCount,
      }}
    >
      {children}
    </PricingExplorerContext.Provider>
  );
};

export const usePricingExplorer = (): PricingExplorerState => {
  const context = useContext(PricingExplorerContext);
  if (!context) {
    throw new Error(
      "usePricingExplorer must be used within a PricingExplorerProvider"
    );
  }
  return context;
};
